import { useState } from "react";
import { Gift, BadgeDollarSign, Tag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export const PricingBadge = ({ u, onClick }) => {
  const base = "inline-flex items-center gap-1 w-max px-2 py-0.5 rounded-full text-[11px] font-semibold border transition-colors";
  if (u.pricing_override === "comp")
    return <button data-testid={`pricing-badge-${u.user_id}`} onClick={onClick} className={`${base} bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25`}><Gift className="h-3 w-3" /> Comp — free</button>;
  if (u.pricing_override === "custom")
    return <button data-testid={`pricing-badge-${u.user_id}`} onClick={onClick} className={`${base} bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30 hover:bg-fuchsia-500/25`}><BadgeDollarSign className="h-3 w-3" /> Custom {money(u.custom_price)}/{u.custom_interval === "year" ? "yr" : "mo"}</button>;
  return <button data-testid={`pricing-badge-${u.user_id}`} onClick={onClick} className={`${base} bg-transparent text-gray-500 border-gray-700 hover:text-amber-300 hover:border-amber-500/40`}><Tag className="h-3 w-3" /> Pricing override</button>;
};

const OPTIONS = [
  { v: "none", t: "Standard price", d: "Billed the normal plan price through Stripe." },
  { v: "comp", t: "Comp (free)", d: "Never charged. Keeps full access and all plan features." },
  { v: "custom", t: "Custom price", d: "Bill a specific amount instead of the plan price." },
];

export const PricingOverrideDialog = ({ user, onClose, onSaved }) => {
  const [mode, setMode] = useState(user?.pricing_override || "none");
  const [amount, setAmount] = useState(user?.custom_price ?? "");
  const [interval, setInterval] = useState(user?.custom_interval || "month");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/admin/users/${user.user_id}/pricing`, { mode, amount: mode === "custom" ? Number(amount) : null, interval });
      toast.success(`Pricing updated for ${user.name || user.email}`, { description: r.data.note || undefined });
      onSaved({ pricing_override: r.data.pricing_override, custom_price: r.data.custom_price, custom_interval: r.data.custom_interval });
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update pricing"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="pricing-dialog" className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><BadgeDollarSign className="h-5 w-5 text-amber-400" /> Pricing Override</DialogTitle>
          <DialogDescription className="text-gray-400 text-sm">Set how <span className="text-gray-200 font-medium">{user?.organization || user?.name || user?.email}</span> is billed.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 pt-2">
          {OPTIONS.map((o) => (
            <button key={o.v} data-testid={`pricing-option-${o.v}`} onClick={() => setMode(o.v)} className={"w-full text-left rounded-xl border px-4 py-3 transition-colors " + (mode === o.v ? "border-amber-500/60 bg-amber-500/10" : "border-gray-800 hover:border-gray-700")}>
              <p className="text-sm font-semibold">{o.t}</p><p className="text-xs text-gray-400 mt-0.5">{o.d}</p>
            </button>
          ))}
        </div>
        {mode === "custom" && (
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Amount (USD)</Label><Input data-testid="pricing-amount" type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="39.00" className="bg-black/30 border-gray-800" /></div>
            <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Billed</Label>
              <select data-testid="pricing-interval" value={interval} onChange={(e) => setInterval(e.target.value)} className="w-full h-9 bg-black/30 border border-gray-800 rounded-md px-2 text-sm text-white">
                <option value="month">Monthly</option><option value="year">Yearly</option>
              </select>
            </div>
          </div>
        )}
        <p className="text-xs text-gray-500 pt-1">If they already pay by card: comp cancels at the end of the paid month; a custom price starts at their next renewal.</p>
        <div className="mt-4 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
          <button data-testid="pricing-save" onClick={save} disabled={saving || (mode === "custom" && !(Number(amount) >= 1))} className="inline-flex items-center gap-2 rounded-lg gold-gradient px-4 py-2 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
